import { GanttPeriod } from '../types/gantt.types'
import { SiteInfo } from '../views/CalendarMultiView'
import { useState, useRef, useCallback, useEffect } from 'react'
import { PDF_DW, BUFFER_DAYS, RH } from '../constants/gantt.const'
import { daysBetween, computeDW, computeVisibleRange, buildDays } from '../constants/gantt.logic'
// 컨테이너 폭·칸 너비·표시 구간·일자 배열 + 팬 오프셋 — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttLayoutArgs = {
  ganttPeriod: GanttPeriod
  selectedSite: SiteInfo | undefined
  ganttBounds: { start: Date; end: Date; }
  holidays: Set<string>
}

export function useGanttLayout({ ganttPeriod, selectedSite, ganttBounds, holidays }: UseGanttLayoutArgs) {
  const [ganttContainerW,    setGanttContainerW]    = useState(0)
  const [ganttOffset,        setGanttOffset]        = useState(0)

  // ── Refs ──────────────────────────────────────────────────────
  const scrollRef          = useRef<HTMLDivElement | null>(null)
  const scrollRoRef        = useRef<ResizeObserver | null>(null)
  const DWRef              = useRef(PDF_DW)
  const ganttContainerWRef = useRef(0)
  const ganttPeriodRef     = useRef<'전체' | '1주' | '2주' | '월'>('전체')
  const isPanModeRef           = useRef(false)
  const ganttOffsetRef         = useRef(0)
  const clampedGanttOffsetRef  = useRef(0)  // 실제 화면에 표시된 시각적 offset (클램프 반영)

  // 1주/2주/월 뷰(팬 모드)에서 현재 화면에 보이는 날짜 범위 — 드래그 중 이 범위를 벗어나면
  // 마일스톤 라벨이 스크롤 뷰포트 밖으로 넘어가 옆 패널 위에 겹쳐 보이므로, 이 범위 안으로 clamp한다.
  const visibleDayRangeRef = useRef<[number, number]>([0, 0])

  // ── Gantt container width (for dynamic DW) ────────────────────
  // 캘린더 등 다른 뷰로 갔다 돌아오면 스크롤 컨테이너 DOM 노드가 새로 만들어진다.
  // useEffect(deps=[loading])로 붙이면 loading 값이 그대로일 때 옵저버가 옛 노드에 남아
  // 폭이 갱신되지 않고(= stale ganttContainerW) 오른쪽에 빈 여백이 생겼다.
  // callback ref로 노드가 바뀔 때마다 재측정 + 재관찰한다.
  const attachScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el
    scrollRoRef.current?.disconnect()
    scrollRoRef.current = null
    if (!el) return
    setGanttContainerW(el.clientWidth)
    const ro = new ResizeObserver(entries => setGanttContainerW(entries[0].contentRect.width))
    ro.observe(el)
    scrollRoRef.current = ro
  }, [])

  useEffect(() => () => { scrollRoRef.current?.disconnect(); scrollRoRef.current = null }, [])

  // 기간 모드·현장 변경 시 팬 오프셋 초기화
  useEffect(() => { setGanttOffset(0) }, [ganttPeriod, selectedSite?.scheduleId])

  // 현장 전환 시 차트 스크롤을 맨 위로(F-11). 왼쪽 리셋은 범위 '전체'일 때만 — 1주·2주·월은 위 팬 규칙이 BUFFER_DAYS 위치를 잡는다.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = 0
    if (ganttPeriod === '전체') el.scrollLeft = 0
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSite?.scheduleId])

  // 팬 모드: 렌더 후 scrollLeft를 버퍼 중앙으로 복원 (부드러운 드래그 기반)
  useEffect(() => {
    if (ganttPeriod !== '전체' && scrollRef.current) {
      scrollRef.current.scrollLeft = BUFFER_DAYS * DWRef.current
    }
  }, [ganttPeriod, ganttOffset, ganttContainerW])

  isPanModeRef.current      = ganttPeriod !== '전체'
  ganttOffsetRef.current    = ganttOffset

  // ── No site ───────────────────────────────────────────────────
  // DW: declared before early returns so closures (handlePDF) avoid TDZ error
  const DW = computeDW(ganttContainerW, ganttPeriod, daysBetween(ganttBounds.start, ganttBounds.end) + 1)
  DWRef.current = DW
  ganttContainerWRef.current = ganttContainerW
  ganttPeriodRef.current = ganttPeriod

  // ── Derived ───────────────────────────────────────────────────
  const today = new Date(); today.setHours(0,0,0,0)

  const vr = computeVisibleRange({ ganttBounds, ganttPeriod, ganttOffset, today })

  const { ganttStart, totalDays } = vr

  if (vr.clampedOffset !== null) clampedGanttOffsetRef.current = vr.clampedOffset

  visibleDayRangeRef.current = vr.visibleDayRange

  const rowH       = RH
  const barTop     = Math.floor((rowH - 20) / 2)

  const DAYS = buildDays({ ganttStart, totalDays, today, holidays })

  return {
    DWRef, ganttContainerWRef, ganttPeriodRef, scrollRef, clampedGanttOffsetRef, isPanModeRef, setGanttOffset,
    visibleDayRangeRef, today, ganttStart, totalDays, DW, rowH, barTop, attachScrollRef, ganttContainerW,
    DAYS,
  }
}
